import { useMemo, useRef, useState } from "react";
import { Select, Spin } from "antd";
import debounce from "lodash/debounce";
import { apiUrlV2 } from "@/consts";
import { TOKEN_KEY } from "@/authProvider";

/** Mirrors the backend `AddressDTO` (src/address/validation.ts). */
export type AddressDTO = {
  placeId?: string | null;
  placeProvider?: string;
  placeUrl?: string | null;
  formattedAddress?: string | null;
  line1: string;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  countryCode: string;
  latitude?: number | null;
  longitude?: number | null;
};

const addressLabel = (a: {
  formattedAddress?: string | null;
  line1?: string | null;
}) => a.formattedAddress || a.line1 || "";

// Unified Places search (GYM-772) lives on the v1 API, not the admin envelope.
async function searchVenues(q: string): Promise<AddressDTO[]> {
  const token = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(
    `${apiUrlV2}/address/search?q=${encodeURIComponent(q)}&limit=8`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!res.ok) return [];
  const body = await res.json();
  return (body.list ?? []) as AddressDTO[];
}

/**
 * Venue address autocomplete. Searches the unified Places API and surfaces the
 * picked `AddressDTO` through `onChange` so the parent can submit it (the
 * backend dedupes/persists it and stores the resolved `addressId`).
 */
export default function AddressSelect({
  value,
  onChange,
  initialLabel,
  id,
}: {
  value: AddressDTO | null;
  onChange: (addr: AddressDTO | null) => void;
  /** Pre-populates the visible label when editing an event with a venue. */
  initialLabel?: string | null;
  id?: string;
}) {
  const [options, setOptions] = useState<AddressDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [typed, setTyped] = useState("");

  const runSearch = useMemo(
    () =>
      debounce(async (q: string) => {
        const results = await searchVenues(q);
        setOptions(results);
        setLoading(false);
      }, 400),
    []
  );

  const handleSearch = (q: string) => {
    setTyped(q);
    if (q.trim().length < 3) {
      setLoading(false);
      setOptions([]);
      runSearch.cancel();
      return;
    }
    setLoading(true);
    runSearch(q.trim());
  };

  // Stable keys: placeId when available, else the formatted label + index.
  const keyRef = useRef<Map<string, AddressDTO>>(new Map());
  keyRef.current = new Map();
  const optionItems = options.map((a, i) => {
    const key = a.placeId || `${addressLabel(a)}-${i}`;
    keyRef.current.set(key, a);
    return { value: key, label: addressLabel(a) };
  });

  // The currently-selected value (or seeded edit label) needs to render even
  // though it isn't in the freshly-searched options list.
  const selectedLabel = value ? addressLabel(value) : initialLabel || undefined;
  const selectedValue = value
    ? value.placeId || addressLabel(value)
    : initialLabel
      ? initialLabel
      : undefined;
  const mergedOptions =
    selectedValue && !optionItems.some((o) => o.value === selectedValue)
      ? [{ value: selectedValue, label: selectedLabel || "" }, ...optionItems]
      : optionItems;

  const searching = loading && typed.trim().length >= 3;

  return (
    <Select
      id={id}
      allowClear
      showSearch
      placeholder="Search for a venue address..."
      filterOption={false}
      loading={searching}
      style={{ width: "100%" }}
      value={selectedValue}
      options={mergedOptions}
      onSearch={handleSearch}
      onChange={(key) => {
        if (!key) {
          onChange(null);
          return;
        }
        const picked = keyRef.current.get(key);
        if (picked) onChange(picked);
      }}
      notFoundContent={
        searching ? (
          <div style={{ textAlign: "center", padding: "8px 0" }}>
            <Spin size="small" />
          </div>
        ) : typed.trim().length >= 3 ? (
          "No addresses found"
        ) : (
          "Type at least 3 characters"
        )
      }
    />
  );
}
